import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  UserCheck,
  UserX,
  Loader2,
  Clock,
  Mail,
  User,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const EmployeeApprovals = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, user: null, action: null });
  const [processing, setProcessing] = useState(false);

  const fetchPendingApprovals = async () => {
    try {
      const response = await api.get('/admin/pending-approvals');
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const handleAction = async () => {
    setProcessing(true);
    try {
      const endpoint = actionDialog.action === 'approve' 
        ? `/admin/approve-user/${actionDialog.user.id}`
        : `/admin/reject-user/${actionDialog.user.id}`;
      
      await api.post(endpoint);
      
      toast.success(
        actionDialog.action === 'approve' 
          ? `${actionDialog.user.name} has been approved`
          : `${actionDialog.user.name}'s registration has been rejected`
      );
      
      setActionDialog({ open: false, user: null, action: null });
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Employee Approvals</h1>
            <p className="text-slate-500 mt-1">Review and approve new employee registrations</p>
          </div>
          {pendingUsers.length > 0 && (
            <Badge className="bg-amber-100 text-amber-700 text-lg px-4 py-2">
              {pendingUsers.length} Pending
            </Badge>
          )}
        </div>

        {/* Content */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : pendingUsers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600"
                            onClick={() => setActionDialog({ open: true, user, action: 'approve' })}
                            data-testid={`approve-${user.id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => setActionDialog({ open: true, user, action: 'reject' })}
                            data-testid={`reject-${user.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <UserCheck className="w-16 h-16 mb-4 text-emerald-200" />
                <p className="text-lg font-medium">No pending approvals</p>
                <p className="text-sm mt-1">All employee registrations have been processed</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">About Employee Approvals</p>
                <p className="text-sm text-blue-700 mt-1">
                  New employee accounts require admin approval before they can log in. 
                  Approved users will receive a notification and can immediately start using the system.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <AlertDialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, user: null, action: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {actionDialog.action === 'approve' ? 'Approve Employee' : 'Reject Registration'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {actionDialog.action === 'approve' ? (
                  <>
                    Are you sure you want to approve <span className="font-medium">{actionDialog.user?.name}</span>? 
                    They will be able to log in and access the system.
                  </>
                ) : (
                  <>
                    Are you sure you want to reject <span className="font-medium">{actionDialog.user?.name}</span>'s registration? 
                    Their account will be removed.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleAction}
                disabled={processing}
                className={actionDialog.action === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}
              >
                {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
